import type { AxiosError } from 'axios';
import type { ApiErrorResponse, ApiFieldError } from './types.js';

/**
 * Erro normalizado das APIs Oprus.
 * Callers nao precisam importar tipos do axios para tratar erros.
 */
export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code?: string;
  public readonly fieldErrors: ApiFieldError[];
  public readonly raw: unknown;
  public readonly isNetworkError: boolean;

  constructor(params: {
    message: string;
    statusCode: number;
    code?: string;
    fieldErrors?: ApiFieldError[];
    raw?: unknown;
    isNetworkError?: boolean;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.statusCode = params.statusCode;
    this.code = params.code;
    this.fieldErrors = params.fieldErrors ?? [];
    this.raw = params.raw;
    this.isNetworkError = params.isNetworkError ?? false;

    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/**
 * Converte um AxiosError em ApiError preservando metadados da API.
 */
export function fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiError {
  const response = error.response;

  if (!response) {
    return new ApiError({
      message: error.message || 'Falha de conexao',
      statusCode: 0,
      code: error.code,
      raw: error,
      isNetworkError: true,
    });
  }

  const data = response.data ?? ({} as ApiErrorResponse);

  return new ApiError({
    message: data.message || error.message || `HTTP ${response.status}`,
    statusCode: response.status,
    code: data.code,
    fieldErrors: data.errors ?? [],
    raw: error,
    isNetworkError: false,
  });
}

/**
 * Codes de rede que devem disparar retry.
 */
export function isNetworkAxiosError(error: AxiosError): boolean {
  return (
    !error.response &&
    (error.code === 'ECONNABORTED' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ENOTFOUND' ||
      error.code === 'ERR_NETWORK' ||
      error.message === 'Network Error')
  );
}
